import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { 
  sendNotification, 
  sendBatchNotifications, 
  isWhatsAppConfigured, 
  MESSAGE_TEMPLATES 
} from '../services/whatsappService';

function NotificationCenter({ onClose }) {
  const [activeTab, setActiveTab] = useState('send');
  const [drivers, setDrivers] = useState([]);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [notificationHistory, setNotificationHistory] = useState([]);
  
  // Send form state
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageType, setMessageType] = useState('CUSTOM');
  const [customMessage, setCustomMessage] = useState('');
  const [selectedLoad, setSelectedLoad] = useState(null);

  useEffect(() => {
    fetchData();
    loadNotificationHistory();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch drivers
      const { data: driversData } = await supabase
        .from('drivers')
        .select('*');
      setDrivers(driversData || []);

      // Fetch recent loads
      const { data: loadsData } = await supabase
        .from('loads')
        .select(`
          *,
          customer:customers(company_name),
          driver:drivers(id, name, phone, user_id(name)),
          load_stops(stop_type, address, time_window_start, time_window_end)
        `)
        .order('created_at', { ascending: false })
        .limit(20);
      setLoads(loadsData || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const loadNotificationHistory = () => {
    try {
      const history = localStorage.getItem('hmgc_notification_history');
      if (history) {
        setNotificationHistory(JSON.parse(history));
      }
    } catch (e) {
      console.error('Error loading notification history:', e);
    }
  };

  const saveNotificationToHistory = (notification) => {
    const newHistory = [notification, ...notificationHistory].slice(0, 50);
    setNotificationHistory(newHistory);
    localStorage.setItem('hmgc_notification_history', JSON.stringify(newHistory));
  };

  const handleSendNotification = async () => {
    if (selectedRecipients.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one recipient' });
      return;
    }

    if (messageType === 'CUSTOM' && !customMessage.trim()) {
      setMessage({ type: 'error', text: 'Please enter a message' });
      return;
    }

    if (messageType !== 'CUSTOM' && !selectedLoad) {
      setMessage({ type: 'error', text: 'Please select a load for this notification type' });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      const recipients = selectedRecipients.map(id => {
        const driver = drivers.find(d => d.id === id);
        return {
          id: driver.id,
          name: driver.name || driver.user_id?.name || 'Driver',
          phone: driver.phone
        };
      });

      let messageContent;
      if (messageType === 'CUSTOM') {
        messageContent = customMessage;
      } else {
        const driver = recipients[0];
        const load = selectedLoad;
        const pickupStop = load?.load_stops?.find(s => s.stop_type === 'pickup');
        const deliveryStop = load?.load_stops?.find(s => s.stop_type === 'delivery');

        switch (messageType) {
          case 'LOAD_ASSIGNED':
            messageContent = MESSAGE_TEMPLATES.LOAD_ASSIGNED(load, driver);
            break;
          case 'STATUS_UPDATE':
            messageContent = MESSAGE_TEMPLATES.STATUS_UPDATE(load, load.status);
            break;
          case 'PICKUP_REMINDER':
            messageContent = MESSAGE_TEMPLATES.PICKUP_REMINDER(load, pickupStop || { address: 'N/A' });
            break;
          case 'DELIVERY_REMINDER':
            messageContent = MESSAGE_TEMPLATES.DELIVERY_REMINDER(load, deliveryStop || { address: 'N/A' });
            break;
          case 'DRIVER_CONFIRMATION_REQUEST':
            messageContent = MESSAGE_TEMPLATES.DRIVER_CONFIRMATION_REQUEST(load);
            break;
          default:
            messageContent = customMessage;
        }
      }

      const results = await sendBatchNotifications(
        recipients,
        'CUSTOM',
        messageContent
      );

      // Save to history
      saveNotificationToHistory({
        id: Date.now(),
        timestamp: new Date().toISOString(),
        recipients: recipients.map(r => r.name).join(', '),
        messageType,
        message: messageContent.substring(0, 100) + (messageContent.length > 100 ? '...' : ''),
        success: results.successful,
        failed: results.failed,
        simulated: results.results[0]?.simulated || false
      });

      if (results.failed === 0) {
        const simNote = results.results[0]?.simulated ? ' (Simulated - WhatsApp not configured)' : '';
        setMessage({ 
          type: 'success', 
          text: `Sent to ${results.successful} recipient(s)${simNote}` 
        });
        setSelectedRecipients([]);
        setCustomMessage('');
        setSelectedLoad(null);
      } else {
        setMessage({ 
          type: 'warning', 
          text: `Sent: ${results.successful}, Failed: ${results.failed}` 
        });
      }
    } catch (err) {
      console.error('Error sending notification:', err);
      setMessage({ type: 'error', text: 'Failed to send: ' + err.message });
    }

    setSending(false);
  };

  const handleSelectAllDrivers = () => {
    if (selectedRecipients.length === drivers.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(drivers.map(d => d.id));
    }
  };

  const toggleRecipient = (driverId) => {
    if (selectedRecipients.includes(driverId)) {
      setSelectedRecipients(selectedRecipients.filter(id => id !== driverId));
    } else {
      setSelectedRecipients([...selectedRecipients, driverId]);
    }
  };

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const contentStyle = {
    backgroundColor: 'var(--surface-1)',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '2rem'
  };

  const tabStyle = (isActive) => ({
    padding: '0.75rem 1.5rem',
    backgroundColor: isActive ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
    color: 'var(--text-primary)',
    border: 'none',
    borderRadius: '5px 5px 0 0',
    cursor: 'pointer',
    fontWeight: isActive ? 'bold' : 'normal',
    marginRight: '0.5rem'
  });

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--accent-primary)' }}>
              📱 Notification Center
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', color: isWhatsAppConfigured() ? 'var(--color-success)' : 'var(--color-warning)', fontSize: '0.9rem' }}>
              {isWhatsAppConfigured() ? '● WhatsApp Connected' : '○ WhatsApp Not Configured (Messages will be simulated)'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        {/* Status Message */}
        {message && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '5px',
            marginBottom: '1rem',
            backgroundColor: message.type === 'success' ? 'var(--color-success)' : 
                           message.type === 'error' ? 'var(--color-danger)' : 
                           message.type === 'warning' ? 'var(--color-warning)' : 'var(--accent-blue)'
          }}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid var(--surface-2)' }}>
          <button style={tabStyle(activeTab === 'send')} onClick={() => setActiveTab('send')}>
            Send Notification
          </button>
          <button style={tabStyle(activeTab === 'history')} onClick={() => setActiveTab('history')}>
            History ({notificationHistory.length})
          </button>
          <button style={tabStyle(activeTab === 'templates')} onClick={() => setActiveTab('templates')}>
            Templates
          </button>
        </div>

        {/* Send Tab */}
        {activeTab === 'send' && (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--accent-primary)' }}>
                Loading...
              </div>
            ) : (
              <>
                {/* Recipients */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                      Recipients ({selectedRecipients.length} selected)
                    </label>
                    <button
                      onClick={handleSelectAllDrivers}
                      style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: 'var(--accent-blue)',
                        color: 'var(--text-primary)',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      {selectedRecipients.length === drivers.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div style={{
                    maxHeight: '150px',
                    overflow: 'auto',
                    backgroundColor: 'var(--surface-2)',
                    borderRadius: '5px',
                    padding: '0.5rem'
                  }}>
                    {drivers.length === 0 ? (
                      <p style={{ color: 'var(--text-muted)', margin: '0.5rem', fontSize: '0.9rem' }}>
                        No drivers found. Add drivers first.
                      </p>
                    ) : (
                      drivers.map(driver => (
                        <label
                          key={driver.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            borderRadius: '3px',
                            backgroundColor: selectedRecipients.includes(driver.id) ? 'var(--bg-primary)' : 'transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedRecipients.includes(driver.id)}
                            onChange={() => toggleRecipient(driver.id)}
                            style={{ marginRight: '0.75rem' }}
                          />
                          <span style={{ color: '#fff' }}>
                            {driver.name || driver.user_id?.name || `Driver #${driver.id}`}
                          </span>
                          <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                            {driver.phone || 'No phone'}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Message Type */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                    Message Type
                  </label>
                  <select
                    value={messageType}
                    onChange={(e) => setMessageType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-2)',
                      border: '1px solid var(--border-default)',
                      borderRadius: '5px',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="CUSTOM">Custom Message</option>
                    <option value="LOAD_ASSIGNED">Load Assignment</option>
                    <option value="STATUS_UPDATE">Status Update</option>
                    <option value="PICKUP_REMINDER">Pickup Reminder</option>
                    <option value="DELIVERY_REMINDER">Delivery Reminder</option>
                    <option value="DRIVER_CONFIRMATION_REQUEST">Confirmation Request</option>
                    <option value="DISPATCH_ALERT">Dispatch Alert</option>
                  </select>
                </div>

                {/* Load Selection (for non-custom messages) */}
                {messageType !== 'CUSTOM' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                      Select Load
                    </label>
                    <select
                      value={selectedLoad?.id || ''}
                      onChange={(e) => {
                        const load = loads.find(l => l.id === parseInt(e.target.value));
                        setSelectedLoad(load);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        backgroundColor: 'var(--surface-2)',
                        border: '1px solid var(--border-default)',
                        borderRadius: '5px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Select a load...</option>
                      {loads.map(load => (
                        <option key={load.id} value={load.id}>
                          #{load.load_number} - {load.customer?.company_name || 'Unknown'} ({load.status})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Custom Message */}
                {messageType === 'CUSTOM' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                      Message
                    </label>
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Enter your message here..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        backgroundColor: 'var(--surface-2)',
                        border: '1px solid var(--border-default)',
                        borderRadius: '5px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                )}

                {/* Send Button */}
                <button
                  onClick={handleSendNotification}
                  disabled={sending || selectedRecipients.length === 0}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    backgroundColor: sending || selectedRecipients.length === 0 ? 'var(--surface-3)' : 'var(--btn-primary-bg)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: sending || selectedRecipients.length === 0 ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  {sending ? 'Sending...' : `Send to ${selectedRecipients.length} Recipient(s)`}
                </button>
              </>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div>
            {notificationHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                No notifications sent yet
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                {notificationHistory.map((notification, index) => (
                  <div
                    key={notification.id || index}
                    style={{
                      backgroundColor: 'var(--surface-2)',
                      borderRadius: '5px',
                      padding: '1rem',
                      marginBottom: '0.75rem'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                        {notification.messageType}
                      </span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        {new Date(notification.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ color: '#fff', marginBottom: '0.5rem' }}>
                      To: {notification.recipients}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      {notification.message}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <span style={{
                        padding: '0.15rem 0.5rem',
                        borderRadius: '3px',
                        fontSize: '0.75rem',
                        backgroundColor: notification.success > 0 ? 'var(--color-success)' : 'var(--color-danger)',
                        color: '#fff'
                      }}>
                        {notification.success} sent
                      </span>
                      {notification.failed > 0 && (
                        <span style={{
                          padding: '0.15rem 0.5rem',
                          borderRadius: '3px',
                          fontSize: '0.75rem',
                          backgroundColor: 'var(--color-danger)',
                          color: '#fff'
                        }}>
                          {notification.failed} failed
                        </span>
                      )}
                      {notification.simulated && (
                        <span style={{
                          padding: '0.15rem 0.5rem',
                          borderRadius: '3px',
                          fontSize: '0.75rem',
                          backgroundColor: 'var(--color-warning)',
                          color: '#fff'
                        }}>
                          Simulated
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
              These are the built-in message templates. They automatically include load and driver details.
            </p>
            
            {Object.entries(MESSAGE_TEMPLATES).map(([key, template]) => (
              <div
                key={key}
                style={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '5px',
                  padding: '1rem',
                  marginBottom: '0.75rem'
                }}
              >
                <div style={{ color: 'var(--accent-primary)', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {key.replace(/_/g, ' ')}
                </div>
                <div style={{ 
                  color: 'var(--text-muted)', 
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'var(--bg-primary)',
                  padding: '0.75rem',
                  borderRadius: '3px'
                }}>
                  {typeof template === 'function' 
                    ? template(
                        { load_number: 'LOAD-001', customer: { company_name: 'Sample Customer' }, commodity: 'General Freight' },
                        { name: 'John Driver' }
                      )
                    : template
                  }
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationCenter;
